import { emptyApi } from "./emptyApi";

export type AccessLevel = "FULL" | "READ_ONLY" | "NO_ACCESS";

export type Permission = {
  permission_key: string;
  access_level: AccessLevel;
};

export type PermissionItem = {
  name: string;
  description: string | null;
  required_by: string[];
  grants_access_to: string[];
};

export type PermissionCategory = {
  category: string;
  description: string | null;
  permissions: PermissionItem[];
};

export type Role = {
  uuid: string;
  name: string;
  role_type: string;
  is_predefined: boolean;
  user_count: number;
};

export type RoleDetail = {
  uuid: string;
  name: string;
  role_type: string;
  is_predefined: boolean;
  permissions: Permission[];
};

export type MyPermissionsResponse = {
  is_super_admin: boolean;
  role: { uuid: string; name: string; role_type: string } | null;
  permissions: Permission[];
};

export const rolesApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getRoles: build.query<Role[], { groupId: string }>({
      query: ({ groupId }) => ({
        url: `/api/inkle/company/v1/group/${groupId}/roles/`,
      }),
      providesTags: ["GroupRoles"],
    }),

    createRole: build.mutation<
      RoleDetail,
      { groupId: string; name: string; permissions: Permission[] }
    >({
      query: ({ groupId, name, permissions }) => ({
        url: `/api/inkle/company/v1/group/${groupId}/roles/`,
        method: "POST",
        body: { name, permissions },
      }),
      invalidatesTags: ["GroupRoles"],
    }),

    getRoleDetail: build.query<RoleDetail, { groupId: string; roleId: string }>(
      {
        query: ({ groupId, roleId }) => ({
          url: `/api/inkle/company/v1/group/${groupId}/roles/${roleId}/`,
        }),
        providesTags: (_result, _error, { roleId }) => [
          { type: "GroupRoles", id: roleId },
        ],
      }
    ),

    updateRole: build.mutation<
      RoleDetail,
      {
        groupId: string;
        roleId: string;
        name?: string;
        permissions?: Permission[];
      }
    >({
      query: ({ groupId, roleId, name, permissions }) => ({
        url: `/api/inkle/company/v1/group/${groupId}/roles/${roleId}/`,
        method: "PUT",
        body: { name, permissions },
      }),
      invalidatesTags: (_result, _error, { roleId }) => [
        "GroupRoles",
        { type: "GroupRoles", id: roleId },
        "MyPermissions",
        "TeamMember",
      ],
    }),

    deleteRole: build.mutation<
      { message: string },
      { groupId: string; roleId: string }
    >({
      query: ({ groupId, roleId }) => ({
        url: `/api/inkle/company/v1/group/${groupId}/roles/${roleId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["GroupRoles"],
    }),

    getPermissionStructure: build.query<
      PermissionCategory[],
      { groupId: string }
    >({
      query: ({ groupId }) => ({
        url: `/api/inkle/company/v1/group/${groupId}/permissions/`,
      }),
    }),

    getMyPermissions: build.query<MyPermissionsResponse, { groupId: string }>({
      query: ({ groupId }) => ({
        url: `/api/inkle/company/v1/group/${groupId}/my-permissions/`,
      }),
      providesTags: ["MyPermissions"],
    }),
  }),
});

export const {
  useGetRolesQuery,
  useCreateRoleMutation,
  useGetRoleDetailQuery,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetPermissionStructureQuery,
  useGetMyPermissionsQuery,
} = rolesApi;
